import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useT } from "@/context/LanguageContext";

export default function Pricing({ tiers = [] }) {
  const { t, ui } = useT();
  return (
    <section id="precios" className="relative bg-[color:var(--gcs-ivory)] py-24 md:py-40" data-testid="pricing-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="md:col-span-4">
            <p className="overline text-[color:var(--gcs-gold)] mb-4">{ui("pricing.chapter")}</p>
            <span className="font-serif italic text-6xl md:text-7xl text-[color:var(--gcs-gold)]/40">/03</span>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-[color:var(--gcs-emerald)]">
              {ui("pricing.h1_a")}<em className="text-[color:var(--gcs-gold)]">{ui("pricing.h1_em")}</em>{ui("pricing.h1_b")}
            </h2>
            <p className="mt-6 text-base md:text-lg text-[color:var(--gcs-emerald)]/75 max-w-2xl leading-relaxed">
              {ui("pricing.lead")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {tiers.map((tier, i) => (
            <motion.div
              key={tier.id || i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={`relative rounded-sm border p-8 md:p-10 flex flex-col ${
                tier.highlighted
                  ? "bg-[color:var(--gcs-emerald)] text-[color:var(--gcs-ivory)] border-[color:var(--gcs-emerald)]"
                  : "bg-[color:var(--gcs-ivory-2)] text-[color:var(--gcs-emerald)] border-[color:var(--gcs-line)]"
              }`}
              data-testid={`pricing-tier-${i}`}
            >
              {tier.highlighted && (
                <span className="absolute -top-3 left-8 rounded-full bg-[color:var(--gcs-gold)] text-[color:var(--gcs-emerald)] px-4 py-1 text-[10px] font-semibold uppercase tracking-widest">
                  {ui("pricing.popular")}
                </span>
              )}
              <div className="mb-6">
                <p className="overline mb-3 text-[color:var(--gcs-gold)]">{t(tier.subtitle)}</p>
                <h3 className="font-serif text-3xl md:text-4xl mb-2">{t(tier.name)}</h3>
                <div className="flex items-baseline gap-2 mt-4">
                  <span className="font-serif text-5xl md:text-6xl leading-none">{tier.price}</span>
                  <span className="text-sm opacity-70">{ui("pricing.per_visit")}</span>
                </div>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {(tier.features || []).map((f, k) => (
                  <li key={k} className="flex items-start gap-3 text-sm leading-relaxed">
                    <Check className="w-4 h-4 shrink-0 mt-0.5 text-[color:var(--gcs-gold)]" />
                    <span>{t(f)}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#contacto"
                className={`inline-flex justify-center rounded-full px-6 py-3 text-sm font-semibold transition-colors duration-500 ${
                  tier.highlighted ? "btn-gold" : "btn-outline-emerald"
                }`}
                data-testid={`pricing-cta-${i}`}
              >
                {ui("cta.choose_plan")} {t(tier.name)}
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
